/**
 * «Внутрішній арбітраж»: the switch; the hour price by the client's tariff
 * and which day's prices the plan uses («ціни РДН на 21.10.2026» / «ціни
 * 20.10.2026 — ціни доби ще не отримано»); the battery plan and actual with
 * SOC; the day's 6 Deye windows with editing. Arbitrage off, a fixed tariff
 * or no plan — the inverter runs its original settings, no windows; with
 * arbitrage off (D2) — «Розклад в інверторі зараз» as read from it. DeyeCloud
 * does not let the settings be read — not managed: the switch does not turn on.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  type DeyeWindow,
  type SiteDayResponse,
  type SiteMoneyResponse,
  type SiteOverviewResponse,
  type SiteResponse,
  type SiteWindowsResponse,
  dayWindowsProblem,
  elapsedHoursOfDay,
} from 'shared';

import { InverterLimits } from '../../../components/InverterLimits.js';
import { InverterProgram } from '../../../components/InverterProgram.js';
import { ProgramChangedWarning } from '../../../components/ProgramChangedWarning.js';
import { RefreshInverterSettings } from '../../../components/RefreshInverterSettings.js';
import { ZeroCurrentWarning } from '../../../components/ZeroCurrentWarning.js';
import { BatteryChart, PriceChart } from '../../../components/charts/Charts.js';
import { byWallClock, cellsOnDay, dayAxis } from '../../../components/charts/day-axis.js';
import { errorText } from '../../../components/errors.js';
import { fmtDate, fmtDateTime, fmtNum, fmtNumShort, fmtTime } from '../../../components/format.js';
import { type ZoneTariff, isZone, ratesFromCells } from '../../../components/tariff.js';
import { WindowsEditor } from '../../../components/windows/WindowsEditor.js';
import { type WindowLimits, followServer, sameWindows } from '../../../components/windows/edit.js';
import { type ProgramSpan, programSummary } from '../../../components/windows/program-summary.js';
import { useSetInternalArbitrage } from '../../../hooks/use-site-results.js';
import { usePhageAccessInfo } from '../../../hooks/use-sites.js';
import { useTariff } from '../../../hooks/use-tariff.js';
import { useLocale } from '../../../i18n/locale-context.js';
import { MoneyModel } from './MoneyCard.js';

type Overview = SiteOverviewResponse['site'];

/** The draft follows the server's windows unless the user has edited them today. */
function useDraft(server: DeyeWindow[] | null, date: string) {
  const [draft, setDraft] = useState<readonly DeyeWindow[] | null>(server);
  const last = useRef({ windows: server as readonly DeyeWindow[] | null, date });
  useEffect(() => {
    const prev = last.current;
    if (prev.date === date && sameWindows(prev.windows, server)) return;
    setDraft((d) => followServer(d, prev, { windows: server, date }));
    last.current = { windows: server, date };
  }, [server, date]);
  return [draft, setDraft] as const;
}

export function ArbitrageCard({
  site,
  overview,
  day,
  windows,
  today,
  nowMs,
  loadKw,
  money,
}: {
  site: SiteResponse;
  overview: Overview;
  day: SiteDayResponse | undefined;
  windows: SiteWindowsResponse | undefined;
  today: string;
  nowMs: number;
  /** Consumption profile, kW per wall-clock hour (24); null without one. */
  loadKw: readonly number[] | null;
  /** «Гроші»: its model goes at the bottom, beside «Хочу керувати» (owner, 25.10.2026). */
  money: SiteMoneyResponse | undefined;
}) {
  const { t, tf, locale } = useLocale();
  const fmt = (v: number, d = 1) => fmtNum(locale, v, d);
  const tz = overview.timezone;
  const toggle = useSetInternalArbitrage(site.id);
  // D4: the inverter is managed through Phage.Energy's access to the station:
  // waiting or closed — no control; no right to manage — switching on again
  // is the retry of the write.
  const access = overview.phage_access;
  const noAccess = access === 'pending' || access === 'closed';
  const enabled = overview.internal_arbitrage_enabled;
  // Owner, 25.10.2026: «Only view» is enough for the data and the calculation;
  // control is the client's separate request — «Хочу керувати»: the rights to
  // give in DeyeCloud, then «Увімкнути керування» (the write checks them).
  const [askControl, setAskControl] = useState(access === 'noedit');
  const company = usePhageAccessInfo().data?.company_name ?? 'Phage.Energy';
  // Owner, 23.10.2026: the original settings not stored (DeyeCloud refuses
  // them, or our program was on the inverter first) — nothing is written.
  const noOriginal = !!site.inverter_settings.unreadable && !site.original_settings;
  const tariff = overview.tariff_type;
  const serverWindows = enabled && windows?.date === today ? windows.windows : null;
  // The autopilot's self-consumption fallback (no prices or profile yet) is no
  // plan: the dispatcher keeps the original settings for it (unless edited by hand).
  const fallback = day?.plan_source === 'self_consumption' && windows?.source !== 'manual';
  const hasPlan = !!serverWindows && day?.date === today && tariff !== 'fixed' && !fallback;
  const [draft, setDraft] = useDraft(hasPlan ? serverWindows : null, today);

  const axis = dayAxis(today, tz, nowMs, tf('site.arb.now', { time: fmtTime(new Date(nowMs), tz) }));
  const n = axis.n;
  const pricesDate = windows?.prices_date ?? day?.plan_prices_date ?? null;
  const zone = isZone(tariff) ? (tariff as ZoneTariff) : null;
  const damPrices = useTariff(site.id, pricesDate, hasPlan && tariff === 'dam' && !!pricesDate);
  // The zone rates the plan reads: 24 wall-clock cells.
  const zoneCells = zone ? site.zone_rates_per_kwh : null;
  let prices: number[] | null = null;
  if (tariff === 'dam' && pricesDate && damPrices.data) {
    // An earlier day's prices are laid onto today by clock hour, as the plan does.
    const p = damPrices.data.hourly_local_per_kwh;
    prices = pricesDate === today ? p.slice(0, n) : byWallClock(p, pricesDate, tz, axis.labels, n);
  } else if (zone && zoneCells) {
    prices = cellsOnDay(zoneCells, axis.labels, n);
  }
  const rates = zone && zoneCells ? zoneRates(zone, zoneCells, (v) => fmt(v, 2)) : null;

  const lim: WindowLimits | null = useMemo(() => {
    if (!windows) return null;
    const blocked: number[] = [];
    for (let p = 1; p < windows.hours; p += 1) {
      if (dayWindowsProblem([{ start_hour: p }], windows.date, tz) !== null) blocked.push(p);
    }
    return {
      hours: windows.hours,
      elapsed: elapsedHoursOfDay(windows.date, tz, new Date(nowMs)),
      pMaxKw: windows.limits.p_max_kw,
      socMinPct: windows.limits.soc_min_pct,
      socMaxPct: windows.limits.soc_max_pct,
      blocked,
      // The plan's limits, kept by a hand edit (owner, 24.10.2026).
      chargeCapKw: windows.limits.charge_cap_kw,
      socFloorPct: windows.limits.soc_floor_pct,
      currentsByPhage: windows.limits.currents_by_phage,
    };
  }, [windows, tz, nowMs]);

  let note: string;
  if (access === 'closed') note = t('site.arb.note.closed');
  else if (access === 'pending') note = t('site.arb.note.pending');
  else if (access === 'noedit' && !enabled) note = t('site.arb.note.noedit');
  else if (!enabled) note = t('site.arb.note.off');
  else if (!tariff) note = t('site.arb.note.tariff_unset');
  else if (tariff === 'fixed') note = t('site.arb.note.fixed');
  else if (!hasPlan) note = t('site.arb.note.noplan');
  else if (tariff === 'dam') {
    note = pricesDate
      ? tf(pricesDate >= today ? 'site.arb.note.dam_today' : 'site.arb.note.dam_old', {
          date: fmtDate(pricesDate),
        })
      : t('site.arb.note.noplan');
  } else if (zone && rates) {
    note = tf(zone === 'zone2' ? 'site.arb.note.zone2' : 'site.arb.note.zone3', rates);
  } else note = t('site.arb.note.zone_norates');

  // The stored plan holds a discharge window's power limit; the inverter
  // discharges only into the object's load, so the expected discharge is the
  // profile's consumption of the hour, up to that limit.
  const expectedPlan = (v: number, h: number): number => {
    const load = loadKw?.[Number(axis.labels[h]?.slice(0, 2))];
    return v > 0 && load !== undefined ? Math.min(v, load) : v;
  };
  const plan = hasPlan && day?.plan_kw ? day.plan_kw.map(expectedPlan) : null;
  const fact = Array.from({ length: n }, (_, h) => day?.actual_kw[h] ?? null);
  const soc = Array.from({ length: n }, (_, h) => day?.soc_pct[h] ?? null);
  const shown = hasPlan ? draft : null;
  const windowOf = (h: number) => {
    if (!shown) return null;
    let k = 0;
    shown.forEach((w, i) => {
      if (w.start_hour <= h) k = i;
    });
    return k + 1;
  };
  const dir = (v: number | null | undefined) =>
    v === null || v === undefined
      ? t('site.tip.no_data')
      : Math.abs(v) < 0.05
        ? t('site.tip.zero')
        : tf(v > 0 ? 'site.tip.discharge' : 'site.tip.charge', { v: fmt(Math.abs(v)) });
  const hourLines = (h: number) => {
    const w = windowOf(h);
    const nowPos = axis.now ?? n;
    return [
      <b key="h">
        {axis.labels[h]}–{axis.labels[h + 1]}
        {w ? ` · ${tf('site.tip.window', { n: w })}` : ''}
      </b>,
      ...(prices?.[h] !== undefined ? [tf('site.tip.price', { v: fmt(prices[h] ?? 0, 2) })] : []),
      ...(plan ? [tf('site.tip.plan', { v: dir(plan[h]) })] : []),
      tf('site.tip.fact', { v: h > nowPos ? t('site.tip.future') : dir(fact[h]) }),
      ...(soc[h] !== null && soc[h] !== undefined ? [tf('site.tip.soc', { v: fmt(soc[h] ?? 0) })] : []),
    ];
  };

  const nowPos = axis.now ?? n;
  let chg = 0;
  let dis = 0;
  fact.forEach((v, h) => {
    if (v === null || h >= nowPos) return;
    const share = Math.min(1, nowPos - h);
    chg += Math.max(-v, 0) * share;
    dis += Math.max(v, 0) * share;
  });
  const limits = `${fmtNumShort(locale, overview.soc_min_pct, 0)}–${fmtNumShort(locale, overview.soc_max_pct, 0)} %`;
  // «Що робитиме установка» (after YASNO's autopilot): the saved program in
  // words — only when it can reach the inverter (access open, originals stored).
  const summary =
    hasPlan && serverWindows && !noAccess && !noOriginal
      ? programSummary(serverWindows, axis.labels, n)
      : null;
  const pct = (v: number) => fmtNumShort(locale, v, 0);
  const spansText = (spans: ProgramSpan[]) =>
    spans.length === 0
      ? t('site.arb.what.none')
      : spans
          .map(
            (s) =>
              `${s.from}–${s.to}${s.socPct !== null ? ` ${tf('site.arb.what.soc', { soc: pct(s.socPct) })}` : ''}`,
          )
          .join(', ');
  const reserveText = site.max_reserve
    ? tf('site.arb.what.max_reserve', { soc: pct(overview.soc_max_pct) })
    : `${pct(Math.min(Math.max(overview.soc_min_pct, site.reserve_pct), overview.soc_max_pct))} %`;
  // D6: no plan without the tariff, the battery's capacity and the inverter's power.
  const noParams = !site.tariff_type || site.capacity_kwh === null || site.p_max_kw === null;
  const switchOff = noAccess || toggle.isPending || overview.mode !== 'internal' || (noParams && !enabled);

  return (
    <section className="card" aria-labelledby="arb-title">
      <div className="card-head">
        <div className="arb-top">
          <h2 id="arb-title">{t('site.arb.title')}</h2>
          {enabled ? (
            <button
              type="button"
              className="switch"
              role="switch"
              aria-checked={enabled}
              disabled={switchOff}
              onClick={() => toggle.mutate(false)}
            >
              <span className="track" aria-hidden="true" />
              <span>{t('site.arb.on')}</span>
            </button>
          ) : (
            <span className="sub">{t('site.arb.off')}</span>
          )}
        </div>
        <span className="sub">{tf('site.arb.sub', { date: fmtDate(today) })}</span>
      </div>
      {noAccess ? <p className="hint">{t('site.arb.no_access_disabled')}</p> : null}
      {noOriginal ? <p className="hint">{t('site.arb.no_original')}</p> : null}
      {enabled && site.original_settings && !noAccess ? (
        <p className="sub">
          {tf('site.arb.orig_saved', { at: fmtDateTime(site.original_settings.saved_at, tz) })}
        </p>
      ) : null}
      <ProgramChangedWarning site={site} />
      <ZeroCurrentWarning site={site} />
      {toggle.error ? (
        <p className="err" role="alert">
          {tf('site.arb.switch_err', { msg: errorText(toggle.error, tf) })}
        </p>
      ) : null}
      <div className="pricenote">{note}</div>
      {summary ? (
        <div>
          <div className="chart-title">{t('site.arb.what.title')}</div>
          <dl className="kv2 what">
            <dt>{t('site.arb.what.charge')}</dt>
            <dd className="num">{spansText(summary.charge)}</dd>
            <dt>{t('site.arb.what.discharge')}</dt>
            <dd className="num">{spansText(summary.discharge)}</dd>
            <dt>{t('site.arb.what.reserve')}</dt>
            <dd className="num">{reserveText}</dd>
          </dl>
        </div>
      ) : null}
      {hasPlan && prices ? (
        <div>
          <div className="chart-title">
            {t(tariff === 'dam' ? 'site.arb.price.dam' : 'site.arb.price.zone')}
          </div>
          <PriceChart
            axis={axis}
            prices={prices}
            fmt={fmt}
            title={t(tariff === 'dam' ? 'site.arb.price.dam' : 'site.arb.price.zone')}
            lines={hourLines}
          />
        </div>
      ) : null}
      <div className="chart-title">{t('site.arb.batt')}</div>
      <div className="legend">
        {plan ? (
          <>
            <span>
              <i className="sw" style={{ background: 'rgba(9,169,249,.22)', border: '1px solid #09a9f9' }} />
              {t('site.arb.legend.plan_charge')}
            </span>
            <span>
              <i className="sw" style={{ background: 'rgba(4,40,93,.22)', border: '1px solid #04285d' }} />
              {t('site.arb.legend.plan_discharge')}
            </span>
          </>
        ) : null}
        <span>
          <i className="sw" style={{ background: '#09a9f9' }} />
          {t('site.arb.legend.fact_charge')}
        </span>
        <span>
          <i className="sw" style={{ background: '#04285d' }} />
          {t('site.arb.legend.fact_discharge')}
        </span>
        <span>
          <i className="sw line" style={{ background: '#04285d' }} />
          {t('site.arb.legend.soc')}
        </span>
        {shown ? (
          <span>
            <i className="sw" style={{ background: '#f3f8fd', border: '1px solid #e3ecf5' }} />
            {t('site.arb.legend.windows')}
          </span>
        ) : null}
      </div>
      <BatteryChart
        axis={axis}
        plan={plan}
        fact={fact}
        soc={soc}
        windows={shown}
        fmt={fmt}
        title={t('site.arb.batt')}
        lines={hourLines}
      />
      {hasPlan && serverWindows && draft && lim && windows ? (
        <WindowsEditor
          siteId={site.id}
          server={{ ...windows, windows: serverWindows }}
          draft={draft}
          setDraft={setDraft}
          lim={lim}
          labels={axis.labels}
          now={axis.now ?? 0}
          tz={tz}
          canWrite={!noAccess}
        />
      ) : null}
      <InverterLimits site={site} />
      {!enabled ? (
        <InverterProgram site={site} canRefresh={!noAccess} />
      ) : !noAccess ? (
        <RefreshInverterSettings site={site} />
      ) : null}
      <div className="daysum">
        {hasPlan ? (
          <>
            <span>
              <span className="k">{t('site.arb.daysum.fact')}</span>{' '}
              <b className="num">
                {fmt(chg)} {t('site.energy.unit')}
              </b>
            </span>
            <span>
              <span className="k">{t('site.arb.daysum.discharge')}</span>{' '}
              <b className="num">
                {fmt(dis)} {t('site.energy.unit')}
              </b>
            </span>
            <span>
              <span className="k">{t('site.arb.daysum.limits')}</span> <b className="num">{limits}</b>
            </span>
          </>
        ) : (
          <span>
            <span className="k">{t('site.arb.daysum.noplan')}</span> <b className="num">{limits}</b>
          </span>
        )}
      </div>
      <MoneyModel money={money} control={enabled} site={site} />
      {!enabled && !noAccess ? (
        <div data-testid="ask-control">
          <div className="actions">
            <button
              type="button"
              className="btn primary"
              aria-expanded={askControl}
              onClick={() => setAskControl((v) => !v)}
            >
              {t('site.arb.control')}
            </button>
          </div>
          {askControl ? (
            <>
              <p className="hint">{tf('site.arb.control.howto', { name: company })}</p>
              <div className="actions">
                <button
                  type="button"
                  className="btn primary"
                  disabled={switchOff}
                  onClick={() => toggle.mutate(true)}
                >
                  {t('site.arb.control.on')}
                </button>
              </div>
            </>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

function zoneRates(
  zone: ZoneTariff,
  cells: readonly number[],
  fmt: (v: number) => string,
): Record<string, string> | null {
  const r = ratesFromCells(zone, cells);
  if (!r) return null;
  const f = (v: number | undefined) => (v === undefined ? '' : fmt(v));
  return { night: f(r.night), day: f(r.day), peak: f(r.peak), half: f(r.half) };
}
